import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import { API, BADGE, fmtPrice, imgUrl } from "@/lib/site";
import { CheckoutDialog } from "@/components/site/CheckoutDialog";
import { PaymentLogos } from "@/components/site/PaymentLogos";
import {
  ArrowLeft, Star, ShieldCheck, Clock, Zap, Users, AlertTriangle,
  CheckCircle2, ExternalLink, Loader2,
} from "lucide-react";

const buyersFor = (slug) => {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) % 100000;
  return 900 + (h % 900);
};

export const ProductDetail = () => {
  const { slug } = useParams();
  const [pack, setPack] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [open, setOpen] = useState(false);
  const [viewers, setViewers] = useState(18);

  useEffect(() => {
    window.scrollTo(0, 0);
    let active = true;
    axios
      .get(`${API}/packs/slug/${slug}`)
      .then((r) => active && setPack(r.data))
      .catch(() => active && setNotFound(true));
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    const id = setInterval(() => setViewers(15 + Math.floor(Math.random() * 16)), 4000);
    return () => clearInterval(id);
  }, []);

  if (notFound) {
    return (
      <div className="relative z-[2] min-h-screen grid place-items-center px-6 text-center">
        <div>
          <h1 className="font-display font-bold text-3xl">Pack nicht gefunden</h1>
          <Link to="/" className="mt-6 inline-block bg-[#e2ff00] text-black font-mono text-sm uppercase tracking-[0.2em] px-6 py-3 rounded-full">Zurück zum Shop</Link>
        </div>
      </div>
    );
  }

  if (!pack) {
    return (
      <div className="relative z-[2] min-h-screen grid place-items-center">
        <Loader2 className="animate-spin text-[#a855f7]" />
      </div>
    );
  }

  const b = BADGE[pack.badge] || BADGE.premium;
  const discount = pack.original_price && pack.original_price > pack.price
    ? Math.round((1 - pack.price / pack.original_price) * 100) : 0;
  const buyers = buyersFor(pack.slug);

  return (
    <div data-testid="product-detail" className="relative z-[2] min-h-screen pb-28">
      {/* header */}
      <header className="sticky top-0 z-40 bg-[#0f0f11]/70 backdrop-blur-xl border-b border-white/10">
        <div className="mx-auto max-w-[1100px] px-5 h-16 flex items-center justify-between">
          <Link data-testid="detail-back" to="/" className="flex items-center gap-2 text-[#8b8b93] hover:text-white transition-colors font-mono text-xs uppercase tracking-[0.15em]">
            <ArrowLeft size={16} /> Shop
          </Link>
          <Link to="/" className="font-display font-extrabold tracking-tight text-lg">OG VAULT</Link>
          <div className="w-16" />
        </div>
      </header>

      <div className="mx-auto max-w-[1100px] px-5 pt-6 lg:grid lg:grid-cols-2 lg:gap-12">
        {/* image */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lg:sticky lg:top-24 lg:self-start">
          <div className="relative rounded-2xl overflow-hidden border border-white/10" style={{ boxShadow: `0 40px 120px -30px ${b.glow}` }}>
            <img src={imgUrl(pack.image)} alt={pack.name} className="w-full aspect-square object-cover object-top" />
            <div className="absolute inset-x-0 bottom-0 p-4">
              <div data-testid="stock-warning" className="flex items-center gap-2 rounded-xl bg-[#f5c518] text-black font-semibold px-4 py-3 text-sm shadow-lg">
                <AlertTriangle size={18} /> Nur noch wenige verfügbar
              </div>
            </div>
          </div>
        </motion.div>

        {/* info */}
        <div className="pt-8 lg:pt-0">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#a855f7]/40 bg-[#a855f7]/10 px-4 py-2 text-[#c99bff] text-sm font-medium mb-6">
            <ShieldCheck size={16} /> Verifizierter Shop seit 2023
          </div>

          <h1 data-testid="detail-name" className="font-display font-extrabold text-4xl md:text-5xl tracking-tight leading-[1.02]">{pack.name}</h1>
          <p className="mt-4 text-lg text-[#8b8b93] leading-relaxed">{pack.subtitle}</p>

          <div className="mt-6 flex flex-wrap gap-2.5">
            {pack.tags.map((t) => (
              <span key={t} className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-[#c9c9d1]">{t}</span>
            ))}
          </div>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex items-center gap-0.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={20} className={i < Math.round(pack.rating) ? "fill-[#f5c518] text-[#f5c518]" : "text-[#3a3a3f]"} />
              ))}
            </div>
            <span className="font-semibold">{pack.rating.toFixed(1)}/5</span>
          </div>
          <div className="mt-3 flex items-center gap-2 text-[#8b8b93]">
            <Users size={18} /> {buyers.toLocaleString("de-DE")}+ verifizierte Käufer
          </div>

          {/* price */}
          <div className="mt-8 flex items-end flex-wrap gap-4">
            <span
              data-testid="detail-price"
              className="font-display font-extrabold text-5xl md:text-6xl leading-none"
              style={{ background: "linear-gradient(90deg,#c084fc,#a855f7)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
            >
              {fmtPrice(pack.price)}
            </span>
            {pack.original_price && (
              <span className="text-2xl text-[#8b8b93] line-through mb-1">{fmtPrice(pack.original_price)}</span>
            )}
            {discount > 0 && (
              <span className="mb-2 rounded-lg bg-[#a855f7]/20 text-[#c99bff] font-bold px-3 py-1.5 text-sm">-{discount}%</span>
            )}
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <Clock size={20} className="text-[#a855f7] mt-0.5" />
              <span className="text-[#c9c9d1]">Sofortige digitale Lieferung</span>
            </div>
            <div className="flex items-start gap-3">
              <ShieldCheck size={20} className="text-[#a855f7] mt-0.5" />
              <span className="text-[#c9c9d1]">Käuferschutz & sichere Zahlungen</span>
            </div>
          </div>

          {/* checkout section */}
          <div className="mt-12 border-t border-white/10 pt-10">
            <h2 className="font-display font-bold text-2xl md:text-3xl leading-tight">
              Sichere dir jetzt dein{" "}
              <span style={{ color: "#c084fc" }}>{pack.name}</span>
            </h2>
            <p className="mt-2 text-[#8b8b93]">Wähle deine bevorzugte Zahlungsmethode</p>

            <div className="mt-5 flex items-center gap-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] px-4 py-3 text-emerald-300 text-sm">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
              </span>
              <span data-testid="live-viewers">{viewers} Personen schauen sich diesen Pack gerade an</span>
            </div>

            <div className="mt-5 rounded-2xl border border-[#a855f7]/30 bg-[#0f0f11] p-6" style={{ boxShadow: "0 0 60px -20px rgba(168,85,247,0.5)" }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-emerald-400 font-mono text-sm font-bold tracking-wide">
                  <CheckCircle2 size={18} /> SICHERER CHECKOUT
                </div>
                <ExternalLink size={16} className="text-[#8b8b93]" />
              </div>
              <h3 className="mt-4 font-display font-bold text-xl">Kreditkarte · Klarna · Apple&nbsp;Pay</h3>
              <p className="mt-1 text-sm text-[#8b8b93]">Sicher & bequem zahlen (über Stripe)</p>

              <div className="mt-5">
                <PaymentLogos />
              </div>

              <button
                data-testid="detail-buy"
                onClick={() => setOpen(true)}
                className="mt-6 w-full flex items-center justify-center gap-2 rounded-full py-4 font-bold text-white text-lg transition-transform hover:scale-[1.01]"
                style={{ background: "linear-gradient(90deg,#7c3aed,#a855f7)", boxShadow: "0 10px 40px -10px rgba(168,85,247,0.7)" }}
              >
                <Zap size={20} className="fill-white" /> Jetzt sicher kaufen
              </button>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-[#8b8b93]">
                <ShieldCheck size={13} /> Digitale Lieferung sofort nach Zahlung
              </p>
            </div>

            <div className="mt-6 flex items-start gap-2 text-sm text-[#8b8b93]">
              <ShieldCheck size={16} className="text-[#a855f7] mt-0.5" />
              Alle Zahlungen sind SSL-verschlüsselt & käuferschutzgesichert
            </div>
            <div className="mt-3 text-sm text-[#8b8b93]">
              ✓ Sofort-Lieferung &nbsp;·&nbsp; ✓ Geld zurück bei Problemen &nbsp;·&nbsp; Verifiziert seit 2023
            </div>
          </div>
        </div>
      </div>

      {/* sticky bottom bar */}
      <div className="fixed bottom-0 inset-x-0 z-40 border-t border-white/10 bg-[#0f0f11]/85 backdrop-blur-xl">
        <div className="mx-auto max-w-[1100px] px-5 py-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#a855f7]">Sofort-Lieferung · SSL-Checkout</div>
              <div className="font-semibold text-sm">{pack.name}</div>
            </div>
            <div className="font-display font-bold text-2xl">{fmtPrice(pack.price)}</div>
          </div>
          <button
            data-testid="detail-buy-sticky"
            onClick={() => setOpen(true)}
            className="mt-3 w-full flex items-center justify-center gap-2 rounded-full py-3.5 font-bold text-white transition-transform hover:scale-[1.01]"
            style={{ background: "linear-gradient(90deg,#7c3aed,#a855f7)" }}
          >
            <Zap size={18} className="fill-white" /> Jetzt sicher kaufen →
          </button>
        </div>
      </div>

      <CheckoutDialog pack={pack} open={open} onOpenChange={setOpen} />
    </div>
  );
};
